import {
  ChangeEvent,
  KeyboardEventHandler,
  useCallback,
  useRef,
  useEffect,
  useState,
} from "react";
import { useHistory } from "react-router-dom";
import styles from "./SearchBox.module.scss";
import SearchIcon from "src/assets/icons/new_search.svg?react";

type Props = {
  handleChangeKeyword?: (value: string) => void;
  handleApplySearch?: (value?: string) => void;
  autoFocus?: boolean;
  onClear?: () => void;
  initialValue?: string;
};

function SearchBox(props: Props) {
  const {
    handleChangeKeyword,
    handleApplySearch,
    autoFocus = false,
    onClear,
    initialValue = "",
  } = props;

  const history = useHistory();
  const [search, setSearch] = useState<string>(initialValue);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setSearch(initialValue);
  }, [initialValue]);

  useEffect(() => {
    if (autoFocus && inputRef.current) {
      inputRef.current.focus();
    }
  }, [autoFocus]);

  const onChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      e.preventDefault();
      e.stopPropagation();
      const value = e.target.value;
      setSearch(value);
      if (handleChangeKeyword) {
        handleChangeKeyword(value);
      }
    },
    [handleChangeKeyword]
  );

  const performSearch = useCallback(() => {
    const searchValue = search.trim();
    
    if (handleApplySearch) {
      // If custom handler is provided, use it
      handleApplySearch(searchValue);
    } else {
      // Default behavior: navigate to lesson library with search keyword
      if (searchValue) {
        history.push(`/lessons?tab=library&search-keyword=${encodeURIComponent(searchValue)}`);
      } else {
        history.push('/lessons?tab=library');
      }
    }
  }, [search, handleApplySearch, history]);

  const onKeyDown: KeyboardEventHandler<HTMLInputElement> = useCallback(
    (e) => {
      if (e.key === "Enter") {
        e.preventDefault();
        performSearch();
      } else if (e.key === "Escape") {
        e.preventDefault();
        setSearch("");
        if (onClear) {
          onClear();
        } else if (handleChangeKeyword) {
          handleChangeKeyword("");
        }
        inputRef.current?.blur();
      }
    },
    [performSearch, onClear, handleChangeKeyword]
  );

  const handleSearchClick = useCallback(() => {
    performSearch();
  }, [performSearch]);

  const handleClear = useCallback(() => {
    setSearch("");
    if (onClear) {
      onClear();
    } else if (handleChangeKeyword) {
      handleChangeKeyword("");
    }
    inputRef.current?.focus();
  }, [onClear, handleChangeKeyword]);

  const handleSearchButtonKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        performSearch();
      }
    },
    [performSearch]
  );

  return (
    <div className={styles.filterBox}>
      <div className={styles.searchWrapper}>
        <div className={styles.emptyTitle}>
          <input
            ref={inputRef}
            id="searchInput"
            placeholder="Search for a Topic or Keyword (e.g., Friendship or Resilience)"
            value={search}
            className={styles.input}
            type="text"
            autoComplete="off"
            onChange={onChange}
            onKeyDown={onKeyDown}
            aria-label="Search for lessons or keywords"
          />
        </div>
      </div>
      <div className={styles.selectViews} onClick={handleSearchClick}>
        <div
          className={styles.searchButton}
          role="button"
          tabIndex={0}
          aria-label="Search"
          onKeyDown={handleSearchButtonKeyDown}
        >
          <SearchIcon className={styles.searchIcon} />
        </div>
      </div>
    </div>
  );
}

export default SearchBox;
